import { db, nowIso, putAndQueue, type FoodRow } from '@/lib/db';
import { upsertCatalogFood } from '@/lib/useCatalog';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { parseOffProduct } from './parse';

export type LookupResult =
  | { status: 'encontrado'; food: FoodRow; fromCache: boolean }
  | { status: 'no_encontrado' }
  | { status: 'sin_datos' }
  | { status: 'sin_servidor' }
  | { status: 'error'; message: string };

/**
 * Busca un producto por código de barras.
 *
 * Orden: primero lo que ya está en el móvil, después la Edge Function. Nunca
 * se llama a Open Food Facts directamente desde el navegador porque su política
 * exige un User-Agent propio y los navegadores prohíben ponerlo.
 */
export async function lookupBarcode(barcode: string): Promise<LookupResult> {
  const local = await db.foods.where('barcode').equals(barcode).first();
  if (local && local.deletedAt === null) {
    return { status: 'encontrado', food: local, fromCache: true };
  }

  if (!isSupabaseConfigured || !supabase) return { status: 'sin_servidor' };

  try {
    const invoked = await supabase.functions.invoke<{ data?: unknown; error?: string }>(
      'off-proxy',
      { body: { barcode } },
    );

    if (invoked.error) {
      const message =
        invoked.error instanceof Error ? invoked.error.message : 'no se pudo consultar el producto';
      return { status: 'error', message };
    }

    const payload = invoked.data;
    if (payload?.data == null) return { status: 'no_encontrado' };

    const parsed = parseOffProduct(payload.data, barcode);
    if (!parsed.found) {
      return parsed.reason === 'sin_datos' ? { status: 'sin_datos' } : { status: 'no_encontrado' };
    }

    // Se guarda en el móvil para que el segundo escaneo no gaste red ni cuota,
    // y entra ya en la búsqueda local.
    const row: FoodRow = { ...parsed.food, updatedAt: nowIso(), deletedAt: null };
    await db.foods.put(row);
    upsertCatalogFood(row);

    return { status: 'encontrado', food: row, fromCache: false };
  } catch (err) {
    return { status: 'error', message: err instanceof Error ? err.message : 'error desconocido' };
  }
}

/**
 * Guarda el peso del envase que el usuario ha tenido que decir a mano. Se
 * encola (así lo saben sus otros dispositivos) y se actualiza el catálogo en
 * memoria: si no, la siguiente vez de la misma sesión se volvía a preguntar.
 */
export async function rememberPackageWeight(foodId: string, grams: number): Promise<void> {
  const food = await db.foods.get(foodId);
  if (!food) return;
  const row: FoodRow = { ...food, packageGrams: grams, updatedAt: nowIso() };
  await putAndQueue('foods', row.id, row);
  upsertCatalogFood(row);
}
